import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../firebase";

interface HomepageContent {
  id: string;
  type: "video" | "image";
  url: string;
  position: number;
  title?: string;
  description?: string;
}

const toYouTubeEmbed = (url: string) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const vid = u.searchParams.get("v");
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    }
    if (u.hostname.includes("youtu.be")) {
      const vid = u.pathname.replace("/", "");
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    }
  } catch {}
  return null;
};

const HomepageDynamic: React.FC = () => {
  const [items, setItems] = useState<HomepageContent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "homepageContents"));
    const unsub = onSnapshot(q, (snap) => {
      const data: HomepageContent[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<HomepageContent, "id">),
      }));
      setItems(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [items]
  );

  // Hide the entire section while loading and when no content exists
  if (loading || sorted.length === 0) {
    return null;
  }

  // Use the first item for the spread layout
  const first = sorted[0];

  return (
    <section className="py-16 bg-gradient-to-b from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Classroom <span className="text-blue-600">Highlights</span>
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl font-semibold text-teal-800">
            Photos and videos from our classes, shared by the admin
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: text */}
          <div>
            <div className="prose prose-lg max-w-none">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  {first.title || 'Classroom moment'}
                </h3>
                {first.description ? (
                  <p className="text-gray-700 leading-relaxed">{first.description}</p>
                ) : (
                  <p className="text-gray-700 leading-relaxed">
                    A glimpse into our classroom activities and learning environment.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right: media */}
          <div className="flex justify-center">
            <div className="w-full h-full overflow-hidden flex items-center justify-center rounded-xl shadow-md border border-gray-100">
              {first.type === 'image' ? (
                <img
                  src={first.url}
                  alt={first.title || 'Classroom highlight'}
                  className="w-full h-full object-cover"
                />
              ) : (
                (() => {
                  const yt = toYouTubeEmbed(first.url);
                  return yt ? (
                    <iframe
                      className="w-full aspect-video"
                      src={yt}
                      title={first.title || 'Classroom video'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <video className="w-full h-full object-cover" src={first.url} controls />
                  );
                })()
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomepageDynamic;
